import { useMutation } from '@tanstack/vue-query'
import { defineStore } from 'pinia'
import axios from '@/plugins/axios'
import { computed, ref } from 'vue'
import router from '@/router'
import { useToast } from 'vue-toastification'
import { permissions } from '@/services/ability'
import type { ApiResponse, User } from '@/types/global'

type OtpOrAuthResult = User | { phone: string } | { enable_phone: boolean }

const requestOtp = async (email: string, password: string, otp?: string, phone?: string) => {
  const response = await axios.post<
    ApiResponse<OtpOrAuthResult> | { error: { code: number; message: string } }
  >('/api/web/session/authenticate/advanced', {
    jsonrpc: '2.0',
    params: { login: email, password: password, db: import.meta.env.VITE_API_DB, otp, phone }
  })

  return response.data
}

const authenticate = async (email: string, password: string, otp: string) => {
  const response = await axios.post('/api/web/session/authenticate/advanced', {
    jsonrpc: '2.0',
    params: { login: email, password: password, db: import.meta.env.VITE_API_DB, otp: otp }
  })
  return response.data
}

const fetchCurrentUser = async () => {
  const response = await axios.post<ApiResponse<User>>('/api/web/session/get_session_info', {
    jsonrpc: '2.0',
    params: {}
  })
  return response.data
}

class AuthStore {
  user = ref<User | null>()

  init = async () => {
    try {
      const response = await fetchCurrentUser()
      this.user.value = response.result
    } catch (error) {
      this.user.value = null
    }
  }

  ability = computed(() => {
    return permissions(this.user.value)
  })

  useLogin = (
    handleRequestOtp: (phone: string) => void,
    handleRequirePhone?: () => void,
    handleError?: (message: string) => void
  ) =>
    useMutation({
      mutationKey: ['login'],
      retry: 3,
      mutationFn: async ({
        email,
        password,
        otp,
        phone
      }: {
        email: string
        password: string
        otp?: string
        phone?: string
      }) => {
        try {
          const data = await requestOtp(email, password, otp, phone)

          return data
        } catch (error) {
          return Promise.reject(error)
        }
      },
      onSuccess: async (data) => {
        if ('error' in data) {
          handleError?.(data.error.message)
          const toast = useToast()
          toast.error('حدث خطأ عند تسجيل الدخول')
          return
        }

        if ('phone' in data.result) {
          handleRequestOtp(data.result.phone)
          return
        }

        if ('enable_phone' in data.result && data.result.enable_phone) {
          handleRequirePhone?.()
          return
        }

        this.user.value = data.result
        localStorage.user = JSON.stringify(data.result)
        sessionStorage.removeItem('otp_phone')
        sessionStorage.removeItem('otp_last_sent')

        sessionStorage.removeItem('otp_sent')
        localStorage.removeItem('selected-session-id')
        await router.push({ name: 'home' })
        const toast = useToast()
        toast.success('تم تسجيل الدخول بنجاح')
        return
      },
      onError: async () => {
        const toast = useToast()
        toast.error('حدث خطأ عند تسجيل الدخول')
      }
    })

  useLogout = () =>
    useMutation({
      mutationKey: ['logout'],
      mutationFn: async () => {
        try {
          const response = await axios.post('/api/web/session/destroy', {
            jsonrpc: '2.0',
            params: {}
          })
          const result = response.data
          if (result) {
            localStorage.clear()
            router.push({ name: 'home' })
          }
        } catch (error) {
          return Promise.reject(error)
        }
      },
      onSuccess: async () => {
        const toast = useToast()
        await router.push({ name: 'login' })
        toast.success('تم تسجيل الخروج بنجاح')
      },
      onError: async () => {
        const toast = useToast()
        toast.error('حدث خطأ عند تسجيل الخروج')
      }
    })
}

export const auth = defineStore('auth', () => new AuthStore())
